import { DataStatus } from "../enums"

/**
 * Interpolate text with key placeholders and their corresponding values.
 *
 * ```js
 * const text = "Hello, {name}! You have {count} new messages."
 *
 * interpolate(text, { name: "John", count: 5 })
 * // Returns: "Hello, John! You have 5 new messages."
 * ```
 *
 * @param cmsText The CMS Text to be interpolated
 * @param values an object containing keys and values to be used for replacement in the CMS text.
 * The keys should match the placeholders in the CMS text, and the values will replace those placeholders.
 * @returns {string} The interpolated string with placeholders replaced by their corresponding values.
 */
export function interpolate(
  cmsText: string | undefined,
  values: Record<string, string | number>,
): string {
  if (!cmsText) return ""
  return Object.entries(values).reduce(
    (text, [key, value]) =>
      text.replace(new RegExp(`{${key}}`, "g"), String(value)),
    cmsText,
  )
}

export const getValueFromCMSMap = (
  code: string,
  map?: Record<string, string>,
): string => {
  if (!map || !code) return code
  return map[code.toLowerCase() as keyof typeof map] ?? code
}

/**
 * Check if any of the specified CMS collections have failed to load in lazy loading mode.
 * @param collectionStatuses - Array of DataStatus values for the collections to check
 * @returns true if any collection has an ERROR status, false otherwise
 */
export const hasCMSCollectionErrors = (
  collectionStatuses: DataStatus[],
): boolean => {
  return collectionStatuses.some((status) => status === DataStatus.ERROR)
}
